import React from 'react';
import isNil from 'lodash/isNil';
import {
  DEFAULT_EMPTY_VALUE,
} from 'core/constants';

type TrendTooltipSeries = {
  key: string;
  color: string;
  label?: string;
};

type TooltipPayload = {
  dataKey?: string | number;
  value?: number;
  payload?: Record<string, string | number | undefined>;
};

type TrendTooltipProps = {
  active?: boolean;
  payload?: TooltipPayload[];
  series: TrendTooltipSeries[];
  formatValue?: (key: string, value: number) => string;
  xKey?: string;
  unit?: string;
  detailKey?: string;
};

function formatReadingValue(
  key: string,
  value: number | undefined,
  formatValue?: (key: string, value: number) => string,
): string {
  let displayValue = DEFAULT_EMPTY_VALUE;
  if (typeof value === 'number') {
    displayValue = formatValue ? formatValue(key, value) : String(value);
  }
  return displayValue;
}

function getReadings(
  series: TrendTooltipSeries[],
  payload: TooltipPayload[] | undefined,
  formatValue?: (key: string, value: number) => string,
) {
  return series.map(({ key, color, label }) => {
    const value = payload?.find((entry) => entry.dataKey === key)?.value;

    return {
      key,
      color,
      label,
      value: formatReadingValue(key, value, formatValue),
    };
  });
}

export default function TrendTooltip({
  active,
  payload,
  series,
  formatValue,
  xKey = 'fullLabel',
  unit,
  detailKey,
}: TrendTooltipProps) {
  const point = payload?.[0]?.payload;
  const dateLabel = point?.[xKey];
  const detail = !isNil(detailKey) ? point?.[detailKey] : undefined;
  const readings = getReadings(series, payload, formatValue);

  return (
    !active || isNil(payload) || payload.length === 0
      ? null
      : (
        <div className="rounded-md border border-gray-200/90 bg-white/95 px-3 py-2 shadow-[0_12px_28px_-16px_rgba(15,23,42,0.45)] backdrop-blur-sm">
          <div className="mb-1 text-[11px] font-medium text-gray-500">
            {dateLabel}
          </div>
          <div className="flex flex-col gap-1 whitespace-nowrap text-sm font-semibold tabular-nums text-gray-950">
            {readings.map((reading) => (
              <span key={reading.key} className="inline-flex items-center gap-1.5">
                <span
                  className="h-1.5 w-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: reading.color }}
                />
                {reading.label && (
                  <span className="text-[11px] font-medium text-gray-500">
                    {reading.label}
                  </span>
                )}
                <span>{reading.value}</span>
              </span>
            ))}
            {unit && (
              <span className="text-[11px] font-medium text-gray-500">{unit}</span>
            )}
          </div>
          {detail && (
            <div className="mt-1 max-w-52 text-xs leading-4 text-gray-500">
              {detail}
            </div>
          )}
        </div>
      )
  );
}
